<template>
  <a-modal
    :visible="props.visible"
    :title="$t('offers.create_new_brand')"
    :okText="$t('common.create')"
    :okButtonProps="{
      props: {
        loading: isFetching
      }
    }"
    @ok="onSubmit"
    @cancel="onClose"
  >
    <a-form-model
      :model="form"
      :rules="formRules"
      ref="formRef"
    >
      <a-form-model-item
        :label="$t('offers.create_form_fields.brand_name')"
        prop="name"
        v-bind="getFieldErrorProps('name')"
      >
        <a-input
          v-model="form.name"
          @input="clearServerError('name')"
        />
      </a-form-model-item>

      <a-form-model-item
        :label="$t('offers.create_form_fields.brand_synonyms')"
        prop="synonyms"
        v-bind="getFieldErrorProps('synonyms')"
      >
        <a-textarea
          v-model="form.synonyms"
          @input="clearServerError('synonyms')"
        />
      </a-form-model-item>
    </a-form-model>
  </a-modal>
</template>

<script setup lang="ts">
import type { FormModel } from "ant-design-vue"
import { message } from "ant-design-vue"
import type { Rule } from "ant-design-vue/es/form"
import { computed, reactive, ref } from "vue"

import useForm from "@/composables/useForm"
import { useServerFormErrors } from "@/composables/useServerFormErrors"
import i18n from "@/i18n"
import { createBrand } from "@/modules/offers/api/productModeration"
import type { CreateBrandFormValues } from "@/modules/offers/types/createOffer"
import type { ProductModerationBrand } from "@/modules/offers/types/offers"
import type { CreateBrandPayload } from "@/modules/offers/types/productModeration"

type Props = {
  visible?: boolean
}
type Emits = {
  (event: "create", payload: ProductModerationBrand): void
  (event: "close"): void
}

const props = withDefaults(defineProps<Props>(), {
  visible: false
})
const emit = defineEmits<Emits>()

const formRef = ref<FormModel>()
const form = reactive<CreateBrandFormValues>({
  name: "",
  synonyms: ""
})

const { validateForm, isValidForm, formValidateRules, resetFields } = useForm(formRef)
const { getFieldErrorProps, setServerErrors, clearServerError, clearServerErrors } =
  useServerFormErrors()

const formRules = computed(() => ({
  name: [
    ...formValidateRules.value.textRequiredRule,
    { max: 150, message: i18n.t("validations.max", { count: 150 }) }
  ],
  synonyms: [{ validator: validateSynonyms }]
}))

const isFetching = ref<boolean>(false)

const validateSynonyms = (_rule: Rule, value: string, callback: Function) => {
  const synonymsArr = value.split(/[ ,\n]/gm).filter(Boolean)
  const uniqueSynonyms = new Set(synonymsArr)

  if (synonymsArr.length !== uniqueSynonyms.size) {
    return callback(new Error(i18n.t("findedDuplicates") as string))
  }

  if (synonymsArr.length > 50) {
    return callback(new Error(i18n.t("validations.max_values_count", { length: 50 }) as string))
  }

  if (synonymsArr.some((item) => item.length > 50)) {
    return callback(new Error(i18n.t("validations.max", { count: 50 }) as string))
  }

  return callback()
}
const onSubmit = async () => {
  if (isFetching.value) return

  clearServerErrors()
  isFetching.value = true
  try {
    await validateForm()

    if (!isValidForm.value) {
      return message.error(i18n.t("offers.offer_form_validate_error") as string)
    }

    const payload: CreateBrandPayload = {
      name_mlang: { uk: form.name },
      synonyms: form.synonyms.split(/[ ,\n]/gm).filter(Boolean)
    }
    const { data } = await createBrand({ payload })
    resetFields()
    clearServerErrors()
    emit("create", data)
  } catch (error) {
    setServerErrors(error, { name_mlang: ["name"], "name_mlang.uk": ["name"] })
  } finally {
    isFetching.value = false
  }
}
const onClose = () => {
  resetFields()
  clearServerErrors()
  emit("close")
}
</script>
